import { createTheme, type Shadows, type ThemeOptions } from "@mui/material/styles";
import { mixHex } from "@/theme/contrast";
import type { MuiText } from "@/theme/muiText";
import tokens from "@/theme/tokens";

// Through --rw-font-sans so fonts.css can swap in a per-script stack
// (:lang(ja) and the like); unset, it is the brand stack itself.
const HEADING_FAMILY = `var(--rw-font-sans, ${tokens.fonts.heading})`;
const BODY_FAMILY = `var(--rw-font-sans, ${tokens.fonts.body})`;
const MONO_FAMILY = tokens.fonts.mono;

// Back-compat helper consumed by AppShell / LoginPage / LandingPage.
// Same keys as before; dark monochrome values.
export const brand = {
  primary: tokens.colors.primary, // white CTA
  secondary: tokens.colors.secondary, // dark secondary
  // The green status accent (it used to name the unused info blue).
  accent: tokens.colors.accent,
  danger: tokens.colors.error,
  success: tokens.colors.success,
  warning: tokens.colors.warning,
  text: tokens.colors.text,
  muted: tokens.colors.muted,
  disabled: tokens.colors.disabled,
  line: tokens.colors.line,
  surface: tokens.colors.surface,
  background: tokens.colors.background,
  page: tokens.colors.page,
  panel: tokens.colors.panel,
  elevated: tokens.colors.elevated,
  chrome: tokens.colors.chrome,
  cover: tokens.colors.chrome,
  ljusgra: tokens.colors.surface,
  fonts: { heading: HEADING_FAMILY, body: BODY_FAMILY, mono: MONO_FAMILY },
};

type Severity = "success" | "info" | "warning" | "error";

type Notice = { accent: string; background: string; border: string; text: string };

/** Filled alerts: the sticky banner at the top of the panel (updated,
 *  restarting, maintenance) and the toasts. MUI paints them in the
 *  palette's ``dark`` shade with text picked against ``main``, which gave
 *  a muddy blue with dark text (3.6:1) and a reload button in grey on
 *  grey. Here they are panels instead, with white text: success, warning
 *  and error a tint of their status colour with that colour on the icon
 *  and the edge; info the neutral Repoworks look (a light grey lift, a
 *  grey edge, a white icon), so "reconnecting" never reads as a success.
 *  Every tint is opaque, so the contrast is the same over any page
 *  (theme/filledAlert.test.tsx measures it). */
function notice(accent: string, tint: number, edge: number): Notice {
  return {
    accent,
    background: mixHex(accent, tokens.colors.panel, tint),
    border: mixHex(accent, tokens.colors.panel, edge),
    text: tokens.colors.text,
  };
}

export const filledAlert: Record<Severity, Notice> = {
  success: notice(tokens.colors.success, 0.16, 0.55),
  info: notice(tokens.colors.icon, 0.07, 0.45),
  warning: notice(tokens.colors.warning, 0.16, 0.55),
  error: notice(tokens.colors.error, 0.16, 0.55),
};

/** Standard and outlined info alerts (hints, "fresh session", empty
 *  states) in the same neutral look as the filled info notice: white text
 *  and icon, a grey lift (standard) or a grey edge (outlined) instead of
 *  MUI's blue. Outlined is transparent, so it is measured on the panel
 *  and the elevated surface (theme/filledAlert.test.tsx). */
export const infoAlert = {
  standard: {
    background: filledAlert.info.background,
    border: tokens.colors.line,
    text: tokens.colors.text,
    icon: tokens.colors.icon,
  },
  outlined: {
    background: "transparent",
    border: filledAlert.info.border,
    text: tokens.colors.text,
    icon: tokens.colors.icon,
  },
} as const;

/** Info chips in the neutral look of the info alerts: a filled chip is
 *  the standard alert's grey lift with its hairline edge (the edge tells
 *  it from a default chip), an outlined one the outlined alert's grey
 *  edge; white text on both. Hover and focus lift the fill a step. The
 *  contrast is measured in theme/filledAlert.test.tsx. */
export const infoChip = {
  filled: {
    background: infoAlert.standard.background,
    hover: mixHex(tokens.colors.icon, tokens.colors.panel, 0.14),
    border: tokens.colors.line,
    text: tokens.colors.text,
  },
  outlined: {
    background: "transparent",
    hover: "rgba(255, 255, 255, 0.08)",
    border: infoAlert.outlined.border,
    text: tokens.colors.text,
  },
} as const;

function infoChipStyle(variant: keyof typeof infoChip) {
  const chip = infoChip[variant];
  const lifted = { backgroundColor: chip.hover };
  return {
    backgroundColor: chip.background,
    color: chip.text,
    border: `1px solid ${chip.border}`,
    "&.MuiChip-clickable:hover": lifted,
    "&.Mui-focusVisible": lifted,
    "& .MuiChip-icon": { color: chip.text },
    "& .MuiChip-deleteIcon": {
      color: chip.text,
      opacity: 0.7,
      "&:hover": { color: chip.text, opacity: 1 },
    },
  };
}

/** Component defaults that carry MUI's built-in text (see muiText.ts).
 *  Without ``muiText`` MUI keeps its own English. */
function muiTextComponents(text: MuiText | undefined): ThemeOptions["components"] {
  if (!text) return {};
  return {
    MuiAutocomplete: {
      defaultProps: {
        clearText: text.autocompleteClear,
        closeText: text.autocompleteClose,
        openText: text.autocompleteOpen,
        loadingText: text.autocompleteLoading,
        noOptionsText: text.autocompleteNoOptions,
      },
    },
    MuiAlert: { defaultProps: { closeText: text.alertClose } },
    MuiBreadcrumbs: { defaultProps: { expandText: text.breadcrumbsExpand } },
  };
}

export function createAppTheme(brand?: {
  primary?: string;
  secondary?: string;
  /** MUI's built-in component text in the UI language. */
  muiText?: MuiText;
}): ReturnType<typeof createTheme> {
  const c = tokens.colors;
  const palette: ThemeOptions["palette"] = {
    mode: "dark",
    // Org overrides omit contrastText so MUI derives a legible one
    // from the actual colour instead of inheriting the Repoworks
    // pairing.
    primary: brand?.primary
      ? { main: brand.primary }
      : { main: c.primary, contrastText: c.primaryText },
    secondary: brand?.secondary
      ? { main: brand.secondary }
      : { main: c.secondary, contrastText: c.text },
    warning: { main: c.warning, contrastText: "#0a0a0a" },
    error: { main: c.error, contrastText: "#ffffff" },
    info: { main: c.info, contrastText: "#0a0a0a" },
    success: { main: c.success, contrastText: "#0a0a0a" },
    text: { primary: c.text, secondary: c.muted, disabled: c.disabled },
    background: { default: c.background, paper: c.surface },
    divider: c.line,
    // Icons are white: IconButton, ListItemIcon and input adornments
    // take ``action.active`` (MUI's dark default is 56 % white).
    action: { active: c.icon },
  };

  // MUI 9 dropped Typography's system props, and with them the dotted
  // palette paths its ``color`` prop took: only palette NAMES
  // ("warning", "textSecondary") still match. About 370 Typographys here
  // say ``color="text.secondary"`` (or "warning.main"…) and all of them
  // rendered in the primary text colour. Map the paths back here rather
  // than rewrite every call site.
  const text = palette.text as Record<"primary" | "secondary" | "disabled", string>;
  const main = (name: "primary" | "secondary" | "error" | "warning" | "info" | "success") =>
    (palette[name] as { main: string }).main;
  const typographyColourPaths = [
    ["text.primary", text.primary],
    ["text.secondary", text.secondary],
    ["text.disabled", text.disabled],
    ...(["primary", "secondary", "error", "warning", "info", "success"] as const).map(
      (name) => [`${name}.main`, main(name)] as const,
    ),
  ].map(([path, colour]) => ({ props: { color: path }, style: { color: colour } }));

  const type = tokens.type;
  const muiText = muiTextComponents(brand?.muiText);
  const rem = (px: number) => `${px / 16}rem`;

  return createTheme({
    palette,
    shape: { borderRadius: tokens.radii.md },
    // No shadows anywhere: surfaces part by colour and a hairline instead.
    shadows: Array<string>(25).fill("none") as Shadows,
    typography: {
      fontFamily: BODY_FAMILY,
      h1: { fontFamily: HEADING_FAMILY, fontWeight: 700, letterSpacing: -0.5 },
      h2: { fontFamily: HEADING_FAMILY, fontWeight: 700, letterSpacing: -0.4 },
      h3: { fontFamily: HEADING_FAMILY, fontWeight: 600, letterSpacing: -0.2 },
      // The start page's heading.
      h4: {
        fontFamily: HEADING_FAMILY,
        fontWeight: type.startHeading.weight,
        fontSize: rem(type.startHeading.size),
      },
      // Panel titles (PageHeader and the like).
      h5: {
        fontFamily: HEADING_FAMILY,
        fontWeight: type.panelTitle.weight,
        fontSize: rem(type.panelTitle.size),
      },
      h6: { fontFamily: HEADING_FAMILY, fontWeight: 600 },
      subtitle1: { fontFamily: HEADING_FAMILY, fontWeight: 500 },
      body1: { fontSize: rem(type.base) },
      body2: { fontSize: rem(type.cardDescription.size) },
      // Meta lines: timestamps, counts, the line under a title.
      caption: { fontSize: rem(type.meta) },
      overline: {
        fontFamily: HEADING_FAMILY,
        letterSpacing: type.overline.letterSpacing,
        fontWeight: type.overline.weight,
        fontSize: rem(type.overline.size),
        textTransform: "uppercase",
      },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0.2 },
    },
    components: {
      ...muiText,
      MuiCssBaseline: {
        styleOverrides: {
          // Flat dark surface — no decorative gradient (the old green/blue
          // radial is gone with the Appli brand).
          body: { backgroundColor: c.page },
          // Thin, dark scrollbars that sit inside the panel they scroll,
          // not the light native ones. ``scrollbar-*`` for Firefox and
          // current Chromium (which then ignores the WebKit rules); the
          // WebKit rules for Safari before 18.2.
          "*": { scrollbarWidth: "thin", scrollbarColor: `${c.line} transparent` },
          "*::-webkit-scrollbar": { width: 8, height: 8 },
          "*::-webkit-scrollbar-track, *::-webkit-scrollbar-corner": { background: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: c.line,
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          code: { fontFamily: MONO_FAMILY },
          pre: { fontFamily: MONO_FAMILY },
          // WCAG 2.4.7 visible focus on dark for non-MUI focusables.
          "a:focus-visible, button:focus-visible, [role='button']:focus-visible, [tabindex='0']:focus-visible":
            { outline: `2px solid ${c.focus}`, outlineOffset: 2 },
        },
      },
      MuiButton: {
        defaultProps: { variant: "contained", disableElevation: true },
        styleOverrides: {
          root: { borderRadius: tokens.radii.md, paddingInline: 16 },
          // White-alpha outline: adapts over any surface and gives the
          // secondary CTA a slightly stronger edge than the line token
          // (which is now ~1.9:1 — fine for cards/dividers).
          outlined: { borderWidth: 1.5, borderColor: "rgba(255,255,255,0.30)" },
        },
        variants: [
          {
            props: { variant: "contained", color: "primary" },
            style: {
              background: c.primary,
              color: c.primaryText,
              "&:hover": { background: "#E6E6E6" },
            },
          },
          {
            props: { variant: "contained", color: "secondary" },
            style: {
              background: c.secondary,
              color: c.text,
              "&:hover": { background: "#363636" },
            },
          },
        ],
      },
      MuiAlert: {
        // Merged with the built-in text defaults (muiTextComponents).
        ...muiText?.MuiAlert,
        styleOverrides: {
          root: ({ ownerState }) => {
            const severity = (ownerState.color ?? ownerState.severity ?? "success") as Severity;
            if (ownerState.variant !== "filled") {
              // Info in the neutral look on every variant, never MUI's
              // blue; the other severities keep MUI's tinted styles.
              if (severity !== "info") return {};
              const info = infoAlert[ownerState.variant === "outlined" ? "outlined" : "standard"];
              return {
                backgroundColor: info.background,
                color: info.text,
                border: `1px solid ${info.border}`,
                "& .MuiAlert-icon": { color: info.icon },
              };
            }
            const notice = filledAlert[severity];
            if (!notice) return {};
            return {
              backgroundColor: notice.background,
              color: notice.text,
              border: `1px solid ${notice.border}`,
              "& .MuiAlert-icon": { color: notice.accent, opacity: 1 },
            };
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: tokens.radii.md, backgroundImage: "none" },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${c.line}`,
            borderRadius: tokens.radii.lg,
            background: c.surface,
            backgroundImage: "none",
          },
        },
      },
      // Flat. The shell's own bar is transparent (AppShell); other app
      // bars, like a dialog's title bar, keep their colour.
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 0,
            boxShadow: "none",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: tokens.radii.sm,
            fontWeight: 600,
            // Info chips ("Central billing", "Update available", a draft)
            // in the neutral info look, never MUI's blue (infoChip).
            ...(ownerState.color === "info"
              ? infoChipStyle(ownerState.variant === "outlined" ? "outlined" : "filled")
              : {}),
          }),
        },
      },
      // A drawer is a panel that slides in over a scrim: panel colour,
      // no edge line (the scrim already parts it from the page).
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.panel,
            backgroundImage: "none",
            border: "none",
          },
        },
      },
      // Menus, selects' lists and popovers sit above a panel of the same
      // colour, and there is no shadow to lift them: the elevated
      // surface and a hairline do it.
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: c.elevated,
            backgroundImage: "none",
            border: `1px solid ${c.line}`,
          },
        },
      },
      MuiAutocomplete: {
        // Merged with the built-in text defaults (muiTextComponents).
        ...muiText?.MuiAutocomplete,
        styleOverrides: {
          paper: {
            backgroundColor: c.elevated,
            backgroundImage: "none",
            border: `1px solid ${c.line}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundImage: "none", border: `1px solid ${c.line}` },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: c.line } } },
      MuiTextField: { defaultProps: { variant: "outlined" } },
      MuiTypography: { variants: typographyColourPaths },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: c.surfaceHover,
            color: c.text,
            border: `1px solid ${c.line}`,
            fontFamily: HEADING_FAMILY,
            fontSize: "0.72rem",
            letterSpacing: 0.4,
          },
          arrow: { color: c.surfaceHover },
        },
      },
    },
  });
}
